/**
 * API 请求模块（基于 HttpOnly Cookie）
 *
 * 认证方式：HttpOnly Cookie（浏览器自动携带同源 Cookie）
 * - 不再从 localStorage 读取 token
 * - 所有 fetch 必须显式 credentials:'same-origin'
 */

import { getI18n } from './i18n';
import { message } from '@ui';

export const API_BASE = `${window.location.origin}/api`;

export interface ApiResult<T = unknown> {
  response: Response;
  data: T & { code?: number; message?: string; data?: unknown };
  unauthorized: boolean;
}

function translate(key: string, fallback: string): string {
  const i18n = getI18n();
  return i18n ? i18n.t(key) : fallback;
}

function notifyError(msg: string): void {
  if (message) message.error(msg);
  else console.error('[API]', msg);
}

/**
 * 统一 API 请求
 */
export async function apiRequest<T = unknown>(
  url: string,
  options: RequestInit = {},
): Promise<ApiResult<T>> {
  const headers: Record<string, string> = { ...(options.headers as Record<string, string>) };

  const i18n = getI18n();
  if (i18n?.getLanguage) {
    headers['Accept-Language'] = i18n.getLanguage();
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
      credentials: options.credentials ?? 'same-origin',
    });

    if (response.status === 401) {
      const data = (await response.json().catch(() => ({
        message: translate('common.unauthorized', 'Unauthorized'),
      }))) as ApiResult<T>['data'];
      notifyError(
        data.message ?? translate('common.loginExpired', 'Login expired, please login again'),
      );
      const auth = (window as unknown as { AuthModule?: { clearUser: () => void } }).AuthModule;
      auth?.clearUser();

      setTimeout(() => {
        if (window.opener) window.close();
        else window.location.href = '/';
      }, 1500);

      return { response, data, unauthorized: true };
    }

    const data = (await response.json()) as ApiResult<T>['data'];
    return { response, data, unauthorized: false };
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
}

/**
 * 管理员 API 请求（HTTP Basic Auth）
 */
export async function adminApiRequest<T = unknown>(
  url: string,
  options: RequestInit = {},
): Promise<ApiResult<T>> {
  try {
    const response = await fetch(url, {
      ...options,
      credentials: options.credentials ?? 'same-origin',
    });
    const data = (await response.json()) as ApiResult<T>['data'];

    if (response.status === 401 || data.code === 401) {
      notifyError(
        data.message ??
          translate(
            'common.adminAuthFailed',
            'Authentication failed, please check admin credentials',
          ),
      );
      setTimeout(() => {
        window.location.href = '/';
      }, 1500);
      return { response, data, unauthorized: true };
    }

    return { response, data, unauthorized: false };
  } catch (error) {
    console.error('Admin API request failed:', error);
    throw error;
  }
}

// ── 兼容层：挂回 window 供未迁移的遗留代码访问 ───────────────────
declare global {
  interface Window {
    API_BASE?: string;
    apiRequest?: typeof apiRequest;
    adminApiRequest?: typeof adminApiRequest;
  }
}

window.API_BASE = API_BASE;
window.apiRequest = apiRequest;
window.adminApiRequest = adminApiRequest;
